import Vue from 'vue'
import VueRouter from 'vue-router'
import UsersPage from '../views/UsersPage.vue'
import Login from '../views/Login.vue'
import Files from '../views/Files.vue'
import FilesEnglish from '../views/FilesEnglish.vue'
import Versions from '../views/Versions.vue'
import Locales from '../views/Locales.vue'
import LocalesEnglish from '../views/LocalesEnglish.vue'
import ContentManagerPage from '../views/ContentManagerPage.vue'
import { useAuthStore } from '@/stores/auth'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },
  {
    path: '/',
    redirect: () => {
      // ✅ Utilisation correcte de Pinia dans les fonctions de redirection
      const authStore = useAuthStore()

      // S'assurer que l'auth est initialisé
      if (!authStore.user) {
        authStore.initializeAuth()
      }

      // Rediriger selon le rôle
      if (authStore.hasRole('content-manager')) {
        return { name: 'ContentManagerPage' }
      } else {
        return { name: 'Users' }
      }
    },
  },
  {
    path: '/users',
    name: 'Users',
    component: UsersPage,
    meta: {
      requiresAuth: true,
      requireRole: 'admin',
    },
  },
  {
    path: '/content',
    name: 'ContentManagerPage',
    component: ContentManagerPage,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/filesUpload',
    name: 'FilesPage',
    component: Files,
    meta: {
      requiresAuth: true,
      requireRole: 'admin',
    },
  },
  {
    path: '/locales',
    name: 'Locales',
    component: Locales,
    meta: {
      requiresAuth: true,
      requireRole: 'admin',
    },
  },
  {
    path: '/client-versions',
    name: 'Versions',
    component: Versions,
    meta: {
      requiresAuth: true,
      requireRole: 'admin',
    },
  },
  {
    path: '/locales/en',
    name: 'LocalesEnglish',
    component: LocalesEnglish,
    meta: {
      requiresPassword: 'QuizFactoryEN11',
    },
  },
  {
    path: '/files/en',
    name: 'FilesEnglish',
    component: FilesEnglish,
    meta: {
      requiresPassword: 'QuizFactoryEN11',
    },
  },
  {
    path: '/forbidden',
    name: 'Forbidden',
    component: () => import('../views/Forbidden.vue'),
  },
  {
    path: '*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue'),
  },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

/**
 * Middleware de vérification de l'authentification
 * @param {Object} to - Route de destination
 * @param {Function} next - Fonction de navigation
 * @returns {Boolean} - true si l'utilisateur est connecté, false sinon
 */
function checkAuthAccess(to, next) {
  // Vérifier si la route nécessite une authentification
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

  if (!requiresAuth) {
    return true // Pas d'authentification requise
  }

  try {
    const authStore = useAuthStore()

    // S'assurer que l'auth est initialisé
    if (!authStore.user) {
      authStore.initializeAuth()
    }

    // Vérifier si l'utilisateur est connecté
    if (authStore.isLoggedIn) {
      return true // Utilisateur connecté
    } else {
      // Rediriger vers la page de login
      next({ name: 'Login' })
      return false // Utilisateur non connecté
    }
  } catch (error) {
    // En cas d'erreur, rediriger vers login par sécurité
    next({ name: 'Login' })
    return false
  }
}

/**
 * Middleware de vérification des mots de passe spéciaux
 * @param {Object} to - Route de destination
 * @param {Function} next - Fonction de navigation
 * @returns {Boolean} - true si l'accès est autorisé, false sinon
 */
function checkPasswordAccess(to, next) {
  // Vérifier si la route nécessite un mot de passe spécial
  const requiredPassword = to.meta.requiresPassword

  if (!requiredPassword) {
    return true // Pas de mot de passe requis
  }

  try {
    const storedPassword = localStorage.getItem('ps')

    // Si le mot de passe est déjà stocké et correct
    if (storedPassword === requiredPassword) {
      return true // Accès autorisé
    }

    // Demander le mot de passe
    let password
    do {
      password = prompt('Mot de passe :')

      // Si l'utilisateur annule, rediriger vers forbidden
      if (password === null) {
        next({ name: 'Forbidden' })
        return false
      }
    } while (password !== requiredPassword)

    // Stocker le mot de passe correct
    localStorage.setItem('ps', password)
    return true // Accès autorisé
  } catch (error) {
    // En cas d'erreur, rediriger vers forbidden par sécurité
    next({ name: 'Forbidden' })
    return false
  }
}

/**
 * Middleware de vérification des rôles
 * @param {Object} to - Route de destination
 * @param {Function} next - Fonction de navigation
 * @returns {Boolean} - true si l'accès est autorisé, false sinon
 */
function checkRoleAccess(to, next) {
  // Vérifier si la route nécessite un rôle spécifique
  const requiredRole = to.meta.requireRole

  if (!requiredRole) {
    return true // Pas de rôle requis, accès autorisé
  }

  try {
    const authStore = useAuthStore()

    // S'assurer que l'auth est initialisé
    if (!authStore.user) {
      authStore.initializeAuth()
    }

    // Vérifier si l'utilisateur a le rôle requis
    if (authStore.hasRole(requiredRole)) {
      return true // Accès autorisé
    } else {
      // Rediriger vers la page interdite
      next({ name: 'Forbidden' })
      return false // Accès refusé
    }
  } catch (error) {
    // En cas d'erreur, rediriger vers forbidden par sécurité
    next({ name: 'Forbidden' })
    return false
  }
}

router.beforeEach((to, _from, next) => {
  // Vérification de l'authentification
  if (!checkAuthAccess(to, next)) {
    return // L'utilisateur n'est pas connecté, la redirection est gérée par checkAuthAccess
  }

  // Vérification des rôles
  if (!checkRoleAccess(to, next)) {
    return // L'accès a été refusé, la redirection est gérée par checkRoleAccess
  }

  // Vérification des mots de passe spéciaux
  if (!checkPasswordAccess(to, next)) {
    return // L'accès a été refusé, la redirection est gérée par checkPasswordAccess
  }

  // Si toutes les vérifications passent, continuer
  next()
})

export default router
